import { useQuery, useZero } from "@rocicorp/zero/react";
import { styled } from "goober";
import { nanoid } from "nanoid";
import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { type EffortLevel } from "../../shared/aiProviders";
import { sharedMutators } from "../../shared/mutators";
import { queries } from "../../shared/queries";
import { useComposerStrip } from "../hooks/useComposerStrip";
import { useCreateSkill } from "../hooks/useCreateSkill";
import { useFileDropzone } from "../hooks/useFileDropzone";
import { useTenantDefaultProviderId } from "../hooks/useScopedModel";
import { useFocusShortcut } from "../hooks/useFocusShortcut";
import { useIsMobile } from "../hooks/useIsMobile";
import { usePlanModeShortcut } from "../hooks/usePlanModeShortcut";
import { mentionConnectorCatalog, useMentions } from "../hooks/useMentions";
import { useSlashSkills } from "../hooks/useSlashSkills";
import { useWorkflowZipImport } from "../hooks/useWorkflowZipImport";
import { prependAttachedSkills } from "../../shared/skills/attachedSkills";
import { prependAttachedConnectors } from "../../shared/connectors/attachedConnectors";
import { toAttachmentPayload } from "../lib/attachments";
import { clientSendContext } from "../lib/clientSendContext";
import { useCaretToEndOnMount, useDraft } from "../lib/draft";
import { emitErrorToast } from "../lib/errorToast";
import { formatElapsedTime } from "../lib/formatElapsedTime";
import { resolveDefaultPlanMode, savePlanMode } from "../lib/planMode";
import { navigate, routes } from "../lib/urls";
import { writeChatPanelOpen } from "../lib/chatPanelOpen";
import { handOffPendingPersonalChat } from "../lib/pendingPersonalChats";
import { usePersonalWorkEnabled } from "../hooks/usePersonalWorkEnabled";
import { useFeatureFlag } from "../hooks/useFeatureFlag";
import { CHAT_WORKFLOWS_FLAG } from "../../shared/featureFlagNames";
import { AnimatedPlaceholder } from "./AnimatedPlaceholder";
import {
  AttachmentPreview,
  AttachmentPreviewRow,
  AttachmentRowAnimator,
  Dropzone,
  marchingDashes,
} from "./AttachmentPreviews";
import { Spinner } from "./Spinner";
import { Button } from "./Button";
import { ComposerStrip } from "./Chat/ComposerStrip";
import { ComposerAddMenu } from "./Chat/ComposerAddMenu";
import { ComposerStripToggle } from "./Chat/ComposerStripToggle";
import { SlashMenu } from "./Chat/SlashMenu";
import { MentionCompletion } from "./Chat/MentionCompletion";
import { SlashSkillCompletion } from "./Chat/SlashSkillCompletion";
import { ConnectorLogo } from "./ConnectorLogo";
import { gettingStartedSuggestions } from "./Chat/workflowSuggestions";
import { CircleMesh, shuffleColors } from "./CircleMesh";
import { FlyingDot } from "./FlyingDot";
import { Icon } from "./Icon";
import {
  InputContainer,
  WORKFLOW_INPUT_HEIGHT,
  WORKFLOW_INPUT_WIDTH,
} from "./InputContainer";
import { StatusIndicator } from "./StatusIndicator";
import { Text } from "./Text";
import { VoiceModeButton } from "./VoiceModeButton";
import { ComposerVoiceRecorder } from "./Chat/ComposerVoiceRecorder";
import {
  StreamingDictation,
  streamingTextareaStyle,
} from "./Chat/StreamingDictation";
import { useVoiceComposer, VOICE_PLACEHOLDER } from "../hooks/useVoiceComposer";

// The form is anchored this far below the hero top and grows downward; the hero is
// sized to keep an equal gap below, so the top never shifts as the input expands.
export const HERO_MIN_GAP = 50;

const CreateContainer = styled("div")`
  height: 100%;
  width: 100%;
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: ${HERO_MIN_GAP}px;
  padding-bottom: 16px;
  box-sizing: border-box;
  z-index: 0;
  flex-shrink: 0;
  transform: translate3d(0, calc(var(--panel-progress, 0) * -16px), 0);
  will-change: transform;
`;

const CircleMeshContainer = styled("div")`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  overflow: hidden;
  transform-origin: center bottom;
  transform: translate3d(0, calc(var(--panel-progress, 0) * 120px), 0)
    scale(calc(1 + var(--overscroll, 0) * 0.03));
  will-change: transform;
`;

const Form = styled("form")`
  padding: 4px;
  gap: 4px;
  border-radius: 16px;
  background: var(--alpha24-invert);
  backdrop-filter: blur(12px);
  display: flex;
  flex-direction: column;
  box-shadow: 0 0 0 0.5px var(--border-alpha);
  opacity: calc(1 - var(--panel-progress, 0) * 1.4);
  transform: translate3d(
    0,
    calc(var(--panel-progress, 0) * 120px - var(--overscroll, 0) * 10px),
    0
  );
  will-change: opacity, transform;
  width: calc(100% - 16px);
  /* +8px covers the form's own 4px padding so the input stays WORKFLOW_INPUT_WIDTH wide on desktop. */
  max-width: calc(${WORKFLOW_INPUT_WIDTH}px + 8px);
`;

const HeadingRow = styled("div")`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 32px;
  padding-right: 8px;
`;

const Heading = styled("div")`
  display: flex;
  align-items: center;
  height: 16px;
  box-sizing: content-box;
  gap: 6px;
  padding: 8px;
`;

const RecordingIndicator = styled("div")`
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 6px;
  background: var(--alpha4);
  border-radius: 50px;
`;

const RecordingDot = styled("div")`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: var(--red-600);
`;

const PROMPT_MAX_HEIGHT = 360;
// Overflowing text fades out before reaching the control rail (mirrors the chat
// composer): fully hidden over the bottom PROMPT_FADE_END px with an 8px fade above,
// and padded clear of the rail so the last line stays legible.
const PROMPT_TEXT_PADDING_BOTTOM = 44;
const PROMPT_FADE_END = 40;

// A fixed box stops CircleMesh from re-measuring and reshuffling its colors as the composer grows the hero; the max(…, 100%) floor still guarantees full coverage if the composer chrome ever outgrows it.
const MESH_HEIGHT = PROMPT_MAX_HEIGHT + HERO_MIN_GAP * 2 + 400;

const MeshViewport = styled("div")`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: max(${MESH_HEIGHT}px, 100%);
`;

const TextareaWrapper = styled(InputContainer)`
  position: relative;
  width: 100%;
  min-height: ${WORKFLOW_INPUT_HEIGHT}px;
  box-sizing: border-box;
  padding: 0;
  background: var(--alpha80-invert);
  transition: background 0ms linear var(--motion-base);

  /* Opaque while the strip is open so the panel tucked behind it doesn't bleed
     through the translucent input; timing mirrors the panel to avoid mid-animation bleed. */
  &[data-strip-open="true"] {
    background: var(--surface-primary);
    transition: background 0ms linear 0ms;
  }
`;

const PromptTextarea = styled("textarea")`
  display: block;
  box-sizing: border-box;
  width: 100%;
  field-sizing: content;
  min-height: ${WORKFLOW_INPUT_HEIGHT}px;
  max-height: ${PROMPT_MAX_HEIGHT}px;
  padding: 12px 12px ${PROMPT_TEXT_PADDING_BOTTOM}px;
  background: transparent;
  color: var(--text-primary);
  border: 0;
  outline: 0;
  resize: none;
  font-size: 12px;
  font-family: InterVariable;
  line-height: 16px;
  overflow-y: auto;
  /* Fade overflowing text out above the control rail only once content scrolls. */
  &[data-overflow="true"] {
    -webkit-mask-image: linear-gradient(
      to bottom,
      black calc(100% - ${PROMPT_FADE_END + 8}px),
      transparent calc(100% - ${PROMPT_FADE_END}px)
    );
    mask-image: linear-gradient(
      to bottom,
      black calc(100% - ${PROMPT_FADE_END + 8}px),
      transparent calc(100% - ${PROMPT_FADE_END}px)
    );
  }
  &::placeholder {
    color: var(--text-primary);
    opacity: 0.3;
  }
`;

const PromptControls = styled("div")`
  position: absolute;
  left: 8px;
  right: 8px;
  bottom: 8px;
  z-index: 2;
  display: flex;
  justify-content: space-between;
  align-items: center;
  pointer-events: none;
`;

const PromptControlGroup = styled("div")`
  display: flex;
  gap: 4px;
  align-items: center;
  pointer-events: auto;
`;

const ComposerColumn = styled("div")`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;
  isolation: isolate;
  width: 100%;
`;

type Props = {
  spaceId: string | null;
  draftKey: string;
  canWrite: boolean;
  headingAction?: ReactNode;
  onFormHeightChange?: (height: number) => void;
  deferQueries?: boolean;
};

export function CreateWorkflow({
  spaceId,
  draftKey,
  canWrite,
  headingAction,
  onFormHeightChange,
  deferQueries,
}: Props) {
  const z = useZero();
  const personalWorkEnabled = usePersonalWorkEnabled() === true;
  const chatWorkflows = useFeatureFlag(CHAT_WORKFLOWS_FLAG) === true;
  const [preferences] = useQuery(queries.userPreferences());
  const defaultPlanMode = resolveDefaultPlanMode(preferences, "workflow");
  const isMobile = useIsMobile();
  const { open: composerStripOpen } = useComposerStrip();
  const [meshSeed] = useState(() => nanoid());
  const [meshColors] = useState(() => shuffleColors());
  const [prompt, setPrompt, clearPrompt] = useDraft(`new-workflow:${draftKey}`);
  const [planMode, setPlanMode] = useState(false);
  const planModeTouchedRef = useRef(false);
  const togglePlanMode = useCallback(() => {
    planModeTouchedRef.current = true;
    setPlanMode((on) => !on);
  }, []);
  usePlanModeShortcut(togglePlanMode);
  const [effort, setEffort] = useState<EffortLevel | null>(null);
  const [chatModel, setChatModel] = useState<string | null>(null);
  const effectiveChatModel = chatModel ?? preferences?.chatModel ?? null;
  const [isTextareaFocused, setIsTextareaFocused] = useState(true);
  const { importWorkflowZip } = useWorkflowZipImport(spaceId, {
    onError: (message) => {
      emitErrorToast({ icon: "paperclip", message, buttonLabel: "Dismiss" });
    },
  });
  const textareaRef = useFocusShortcut<HTMLTextAreaElement>();
  useCaretToEndOnMount(textareaRef);
  const scrollPromptToEnd = useCallback(() => {
    const el = textareaRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [textareaRef]);
  const [promptOverflow, setPromptOverflow] = useState(false);
  const updatePromptOverflow = useCallback(() => {
    const el = textareaRef.current;
    setPromptOverflow(!!el && el.scrollHeight > el.clientHeight + 1);
  }, [textareaRef]);
  const voice = useVoiceComposer({
    textareaRef,
    value: prompt,
    setValue: setPrompt,
    canWrite,
    onStream: () => {
      scrollPromptToEnd();
      updatePromptOverflow();
    },
  });
  const isRecording = voice.isRecording;
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [prefetchSkills, setPrefetchSkills] = useState(false);
  const {
    availableSkills,
    selectedSkills,
    addSkill,
    removeSkill,
    clearSkills,
    syncSlashToken,
    slashCompletion,
    handleSlashKeyDown,
  } = useSlashSkills({
    spaceId,
    textareaRef,
    value: prompt,
    setValue: setPrompt,
    onAfterAccept: () => {
      scrollPromptToEnd();
      updatePromptOverflow();
    },
    enabled: !deferQueries,
    menuOpen: showSuggestions || prefetchSkills,
  });
  const [allConnectors] = useQuery(queries.connectorCatalog());
  const mentionTenantConnectors = useMemo(() => {
    const seen = new Set<string>();
    return allConnectors.flatMap((app) => {
      if (seen.has(app.name)) return [];
      seen.add(app.name);
      return [{ name: app.name, identifier: app.identifier }];
    });
  }, [allConnectors]);
  const {
    selectedConnectors,
    addConnector,
    removeConnector,
    clearConnectors,
    searchConnectors,
    syncMentionToken,
    mentionCompletion,
    handleMentionKeyDown,
  } = useMentions({
    textareaRef,
    value: prompt,
    setValue: setPrompt,
    steps: [],
    connectors: [],
    tenantConnectors: mentionTenantConnectors,
    connectorCatalog: mentionConnectorCatalog,
    onAfterAccept: () => {
      scrollPromptToEnd();
      updatePromptOverflow();
    },
  });
  const { canCreate: canCreateSkill, createSkill } = useCreateSkill(
    spaceId ?? undefined
  );
  const openNewSkill = () => {
    const id = createSkill();
    if (id) navigate(routes.modal.skill, { id });
  };
  const formRef = useRef<HTMLFormElement | null>(null);
  const measureForm = useCallback(() => {
    const el = formRef.current;
    if (el) onFormHeightChange?.(el.offsetHeight);
  }, [onFormHeightChange]);
  useLayoutEffect(() => {
    const el = formRef.current;
    if (!el || !onFormHeightChange) return;
    measureForm();
    const ro = new ResizeObserver(measureForm);
    ro.observe(el);
    return () => ro.disconnect();
  }, [measureForm, onFormHeightChange]);
  // Re-measure synchronously when the slash menu opens/closes so the hero is at
  // its final height before paint — otherwise the menu flashes at the old height.
  useLayoutEffect(() => {
    measureForm();
  }, [showSuggestions, measureForm]);
  const placeholderPromptRef = useRef("");
  const modelProviderId = useTenantDefaultProviderId({
    enabled: !deferQueries,
    chatModel: effectiveChatModel,
  });
  const {
    pendingAttachments,
    isUploading,
    isDragging,
    flyingFiles,
    processFiles,
    removeAttachment,
    clearAttachments,
    removeFlyingFile,
    handlePaste,
  } = useFileDropzone({
    enabled: canWrite,
    providerId: modelProviderId,
    onZipFile: importWorkflowZip,
    acceptAnyFile: true,
  });

  const handlePlaceholderPromptChange = useCallback((text: string) => {
    placeholderPromptRef.current = text;
  }, []);

  useEffect(() => {
    if (!planModeTouchedRef.current) setPlanMode(defaultPlanMode);
  }, [defaultPlanMode]);

  const createWorkflow = (text: string, isDictated?: boolean) => {
    if (!canWrite || !spaceId || isUploading) return;
    const hasText = !!text.trim();
    const hasAttachments = pendingAttachments.length > 0;
    const hasSkills = selectedSkills.length > 0;
    const hasConnectors = selectedConnectors.length > 0;
    if (!hasText && !hasAttachments && !hasSkills && !hasConnectors) return;

    const attachments = toAttachmentPayload(pendingAttachments);

    const workflowId = nanoid(12);
    const branchId = nanoid(12);
    const commitId = nanoid(12);
    const chatId = nanoid(12);
    savePlanMode(chatId, planMode);
    z.mutate(
      sharedMutators.createWorkflow({
        spaceId,
        id: workflowId,
        branchId,
        commitId,
        chatId,
        userEntryId: nanoid(12),
        name: "Untitled",
        prompt: prependAttachedConnectors(
          prependAttachedSkills(text.trim(), selectedSkills),
          selectedConnectors
        ),
        isDictated,
        planMode,
        effort: effort ?? undefined,
        chatModel: effectiveChatModel ?? undefined,
        attachments,
        ...clientSendContext(),
      })
    );
    // The new workflow opens straight into its own chat, without waiting for the chat to sync.
    const personalChat =
      personalWorkEnabled && chatWorkflows
        ? { id: nanoid(12), chatId: nanoid(12) }
        : null;
    if (personalChat) {
      z.mutate(sharedMutators.createPersonalChat(personalChat));
      z.mutate(
        sharedMutators.setChatFocus({
          id: personalChat.id,
          entryId: nanoid(12),
          workflowId,
          branchId,
          workflowName: "Untitled",
          branchName: null,
        })
      );
      handOffPendingPersonalChat(personalChat);
      writeChatPanelOpen(true);
    }
    clearAttachments();
    clearSkills();
    clearConnectors();
    clearPrompt();
    navigate(routes.space.workflow, {
      spaceId,
      workflowId,
      branchId,
      ...(personalChat && { chatId: personalChat.id, navigation: "source" }),
    });
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    createWorkflow(prompt);
  };

  return (
    <CreateContainer>
      <CircleMeshContainer
        style={{
          height: `calc(var(--hero-height, ${isMobile ? 160 : 240}px) + 120px)`,
        }}
      >
        <MeshViewport>
          <CircleMesh
            seed={meshSeed}
            targetCircleSize={95}
            mutingIntensity={0.6}
            weights={[400, 600]}
            restrictedColors={meshColors}
          />
        </MeshViewport>
      </CircleMeshContainer>

      <Form ref={formRef} onSubmit={handleSubmit}>
        <HeadingRow>
          <Heading>
            <StatusIndicator
              state={isDragging ? "dragging" : "resting"}
              focused={
                isDragging || isTextareaFocused || isRecording || planMode
              }
              planMode={planMode}
            />
            <Text
              variant="text-small-md"
              color="var(--text-primary)"
              style={{
                opacity:
                  isDragging || isRecording || isTextareaFocused || planMode
                    ? 1
                    : 0.2,
              }}
            >
              {isDragging
                ? "Let’s take a look"
                : isRecording
                  ? "Listening…"
                  : planMode
                    ? "What should we plan?"
                    : "What do you want to do?"}
            </Text>
          </Heading>
          {isRecording ? (
            <RecordingIndicator>
              <RecordingDot />
              <Text variant="text-small-md" color="var(--text-secondary)">
                {formatElapsedTime(voice.elapsed)}
              </Text>
            </RecordingIndicator>
          ) : (
            headingAction
          )}
        </HeadingRow>
        <AttachmentRowAnimator
          visible={
            pendingAttachments.length > 0 ||
            selectedSkills.length > 0 ||
            selectedConnectors.length > 0
          }
        >
          <AttachmentPreviewRow
            style={{
              flexWrap: "nowrap",
              maxWidth: "100%",
            }}
          >
            {selectedSkills.map((skill) => (
              <AttachmentPreview
                key={skill.id}
                onPress={() => removeSkill(skill.id)}
              >
                <span className="file-icon">
                  <Icon name="skills" size={10} />
                </span>
                <span className="remove-icon">
                  <Icon name="x" size={10} />
                </span>
                <Text variant="text-small-md" ellipsis>
                  {skill.name}
                </Text>
              </AttachmentPreview>
            ))}
            {selectedConnectors.map((connector) => (
              <AttachmentPreview
                key={connector.name}
                onPress={() => removeConnector(connector.name)}
              >
                <span className="file-icon">
                  <ConnectorLogo
                    template={{
                      name: connector.name,
                      identifier: connector.identifier,
                    }}
                    size={10}
                  />
                </span>
                <span className="remove-icon">
                  <Icon name="x" size={10} />
                </span>
                <Text variant="text-small-md" ellipsis>
                  {connector.name}
                </Text>
              </AttachmentPreview>
            ))}
            {pendingAttachments.map((att) => (
              <AttachmentPreview
                key={att.id}
                onPress={() => removeAttachment(att.id)}
              >
                <span className="file-icon">
                  {att.remoteId ? (
                    <Icon
                      name={
                        att.mediaType === "application/pdf"
                          ? "file-scan"
                          : "paperclip"
                      }
                      size={10}
                    />
                  ) : (
                    <Spinner size={10} />
                  )}
                </span>
                <span className="remove-icon">
                  <Icon name="x" size={10} />
                </span>
                <Text variant="text-small-md" ellipsis>
                  {att.fileName}
                </Text>
              </AttachmentPreview>
            ))}
          </AttachmentPreviewRow>
        </AttachmentRowAnimator>
        <ComposerColumn>
          {showSuggestions && (
            <SlashMenu
              onSelect={(p) => createWorkflow(p)}
              onSelectSkill={(skill) => {
                addSkill(skill);
                requestAnimationFrame(() => textareaRef.current?.focus());
              }}
              onDismiss={() => setShowSuggestions(false)}
              suggestions={gettingStartedSuggestions}
              skills={availableSkills}
              canCreateSkill={canCreateSkill}
              onCreateSkill={openNewSkill}
            />
          )}
          <TextareaWrapper
            data-composer
            data-strip-open={composerStripOpen}
            style={showSuggestions ? { display: "none" as const } : undefined}
          >
            <PromptTextarea
              autoFocus={!isMobile}
              ref={textareaRef}
              value={prompt}
              readOnly={!canWrite || isRecording}
              placeholder={isRecording ? VOICE_PLACEHOLDER : undefined}
              data-overflow={promptOverflow || undefined}
              onChange={(e) => {
                const shouldFollowCaret =
                  e.target.selectionStart === e.target.value.length &&
                  e.target.selectionEnd === e.target.value.length;
                setPrompt(e.target.value);
                syncSlashToken(e.target);
                syncMentionToken(e.target);
                requestAnimationFrame(() => {
                  if (shouldFollowCaret) scrollPromptToEnd();
                  updatePromptOverflow();
                });
              }}
              onScroll={updatePromptOverflow}
              onKeyUp={(e) => {
                syncSlashToken(e.currentTarget);
                syncMentionToken(e.currentTarget);
              }}
              onClick={(e) => {
                syncSlashToken(e.currentTarget);
                syncMentionToken(e.currentTarget);
              }}
              onSelect={(e) => {
                syncSlashToken(e.currentTarget);
                syncMentionToken(e.currentTarget);
              }}
              onFocus={() => setIsTextareaFocused(true)}
              onBlur={() => setIsTextareaFocused(false)}
              onPaste={handlePaste}
              onKeyDown={(e) => {
                if (isRecording) return;
                if (handleSlashKeyDown(e)) return;
                if (handleMentionKeyDown(e)) return;
                if (
                  e.key === "/" &&
                  !e.metaKey &&
                  !e.ctrlKey &&
                  canWrite &&
                  prompt === ""
                ) {
                  e.preventDefault();
                  setShowSuggestions(true);
                  return;
                }
                if (
                  e.key === "Tab" &&
                  !prompt &&
                  placeholderPromptRef.current
                ) {
                  e.preventDefault();
                  setPrompt(placeholderPromptRef.current);
                  requestAnimationFrame(scrollPromptToEnd);
                } else if (
                  e.key === "Enter" &&
                  !e.shiftKey &&
                  !e.altKey &&
                  !e.ctrlKey
                ) {
                  e.preventDefault();
                  createWorkflow(prompt);
                }
              }}
              style={{
                ...(isDragging && { color: "transparent" }),
                ...(voice.streamingRange && streamingTextareaStyle),
                ...(slashCompletion.suffix !== "" && {
                  color: "transparent",
                  caretColor: "var(--text-primary)",
                }),
              }}
            />
            <StreamingDictation
              textareaRef={textareaRef}
              value={prompt}
              range={voice.streamingRange}
            />
            <SlashSkillCompletion
              textareaRef={textareaRef}
              value={prompt}
              isTextareaFocused={isTextareaFocused}
              completion={slashCompletion}
            />
            <MentionCompletion
              textareaRef={textareaRef}
              value={prompt}
              isTextareaFocused={isTextareaFocused}
              completion={mentionCompletion}
            />
            {!isDragging && (
              <PromptControls>
                <PromptControlGroup>
                  <ComposerAddMenu
                    onAddFiles={processFiles}
                    pickers={{
                      skills: availableSkills,
                      onAddSkill: addSkill,
                      searchConnectors,
                      onAddConnector: addConnector,
                      onCreateSkill: canCreateSkill ? openNewSkill : undefined,
                      onPrefetch: () => setPrefetchSkills(true),
                    }}
                    disabled={!canWrite}
                  />
                </PromptControlGroup>
                {isRecording ? (
                  <ComposerVoiceRecorder
                    {...voice.recorder}
                    onSend={(text) => {
                      const value = voice.finish(text);
                      if (isUploading) {
                        setPrompt(value);
                        return;
                      }
                      createWorkflow(value, true);
                    }}
                  />
                ) : (
                  <>
                    <ComposerStripToggle />
                    <PromptControlGroup>
                      <VoiceModeButton
                        disabled={!canWrite}
                        disabledReason="You don’t have permission to create workflows"
                        onClick={voice.start}
                      />
                      <Button
                        icon="arrow-up"
                        type="submit"
                        disabled={
                          !canWrite ||
                          !spaceId ||
                          isUploading ||
                          (!prompt.trim() &&
                            pendingAttachments.length === 0 &&
                            selectedSkills.length === 0)
                        }
                      />
                    </PromptControlGroup>
                  </>
                )}
              </PromptControls>
            )}
            {isDragging && (
              <Dropzone
                onDragOver={(e) => e.preventDefault()}
                onDrop={(e) => e.preventDefault()}
              >
                <svg
                  width="100%"
                  height="100%"
                  style={{ position: "absolute", inset: 0 }}
                >
                  <rect
                    x="1"
                    y="1"
                    width="calc(100% - 2px)"
                    height="calc(100% - 2px)"
                    rx="12"
                    ry="12"
                    fill="none"
                    stroke="var(--text-primary)"
                    strokeWidth="1"
                    strokeDasharray="6 4"
                    style={{
                      animation: `${marchingDashes} 500ms linear infinite`,
                    }}
                  />
                </svg>
                <Icon name="arrow-down" size={10} color="var(--text-primary)" />
                <Text variant="text-small" color="var(--text-primary)">
                  Drop files here
                </Text>
              </Dropzone>
            )}
            <div style={isDragging ? { visibility: "hidden" } : undefined}>
              <AnimatedPlaceholder
                visible={!prompt && !isRecording}
                onPromptChange={handlePlaceholderPromptChange}
              />
            </div>
          </TextareaWrapper>
          <ComposerStrip
            canWrite={canWrite}
            inset={false}
            modelScope={{ type: "personal" }}
            effort={effort}
            onEffortChange={setEffort}
            chatModel={effectiveChatModel}
            onChatModelChange={setChatModel}
            deferModelQueries={deferQueries}
            planMode={planMode}
            onPlanModeToggle={togglePlanMode}
          />
        </ComposerColumn>
      </Form>
      {flyingFiles.map((f) => (
        <FlyingDot
          key={f.id}
          startX={f.x}
          startY={f.y}
          onDone={() => removeFlyingFile(f.id)}
        />
      ))}
    </CreateContainer>
  );
}
